import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Box, Button, GlobalStyles, Grid, Stack, Typography, Menu, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { useMemo, useRef, useState, useCallback } from 'react';

import {
  CalendarEvent,
  CalendarEventModel,
  EventCategory,
  ReminderType,
  RecurringPattern,
  RecurringPeriod,
  ReminderUnit,
} from '@/types/calendar';

import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { 
  useGetCommunityCalendarEvents,
  QUERY_KEY 
} from '@/api/endpoints/calendar/useGetCommunityCalendarEvents';
import { PlusIcon } from '@/assets/icons/PlusIcon';

import CommunityCalendarEventCreateModal from './CommunityCalendarEventCreateModal';
import CommunityCalendarEventEditModal from './CommunityCalendarEventEditModal';
import CommunityCalendarFirstTimeExperience from './CommunityCalendarFirstTimeExperience';
import CommunityEventDetailsModal from './CommunityEventDetailsModal';

type CalendarViewType = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';

// Event color mappings matching Figma design
const EVENT_COLORS = {
  default: {
    bg: '#FAFAFA',
    border: '#E9EAEB',
    text: '#414651',
    timeText: '#535862',
  },
  blue: {
    bg: '#EFF8FF',
    border: '#B2DDFF',
    text: '#175CD3',
    timeText: '#1570EF',
  },
  purple: {
    bg: '#F9F5FF',
    border: '#E9D7FE',
    text: '#6941C6',
    timeText: '#7F56D9',
  },
  indigo: {
    bg: '#EEF4FF',
    border: '#C7D7FE',
    text: '#3538CD',
    timeText: '#444CE7',
  },
  green: {
    bg: '#EDFCF2',
    border: '#AAF0C4',
    text: '#087443',
    timeText: '#099250',
  },
  orange: {
    bg: '#FEF6EE',
    border: '#F9DBAF',
    text: '#B93815',
    timeText: '#E04F16',
  },
  pink: {
    bg: '#FDF2FA',
    border: '#FCCEEE',
    text: '#C11574',
    timeText: '#DD2590',
  },
  yellow: {
    bg: '#FEFBE8',
    border: '#FEEE95',
    text: '#A15C07',
    timeText: '#CA8504',
  },
  lightPurple: {
    bg: '#F4F3FF',
    border: '#D9D6FE',
    text: '#5925DC',
    timeText: '#6938EF',
  },
} as const;

interface CalendarEventContentProps {
  event: {
    title: string;
    extendedProps: {
      color: (typeof EVENT_COLORS)[keyof typeof EVENT_COLORS];
    };
  };
}

const CalendarEventContent = ({ event }: CalendarEventContentProps) => {
  const { color } = event.extendedProps;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box
        sx={{
          fontWeight: 600,
          fontSize: '12px',
          color: color.text,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {event.title}
      </Box>
    </Box>
  );
};

interface DisplayEvent {
  id: string | number;
  title: string;
  start: string;
  end: string;
  all_day: boolean;
  description?: string | null;
  isAiEvent?: boolean;
  community_id?: number;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  community?: {
    id: number;
    name: string;
  };
  post?: {
    id: number;
    title: string;
  };
  poll?: {
    id: number;
    question: string;
  };
  groupNegotiation?: {
    id: number;
    title: string;
  };
  category?: EventCategory;
  reminderType?: ReminderType;
  recurringPattern?: RecurringPattern;
  recurringEvery?: number;
  recurringPeriod?: RecurringPeriod;
  neverExpires?: boolean;
  reminderIntervals?: Array<{ value: number; unit: ReminderUnit }>;
  notifySelf?: boolean;
  emailNotification?: boolean;
  smsNotification?: boolean;
  pushNotification?: boolean;
  dashboardNotification?: boolean;
  reminders?: Array<{
    id: number;
    calendar_event_id: number;
    remind_at: string;
    type: string;
    unit: string;
    value: number;
    sent: boolean;
    created_at: string;
    updated_at: string;
  }>;
  recurringSettings?: {
    id: number;
    calendar_event_id: number;
    pattern: string;
    every: number;
    period: string;
    end_date: string | null;
    created_at: string;
    updated_at: string;
  };
  metadata?: {
    category?: string;
    notifySelf?: boolean;
    smsNotification?: boolean;
    pushNotification?: boolean;
    emailNotification?: boolean;
    dashboardNotification?: boolean;
  };
}

interface CommunityCalendarViewProps {
  communityId: number;
  communityName?: string;
  isUserMember?: boolean;
  isUserAdmin?: boolean;
}

const CommunityCalendarView = ({
  communityId,
  communityName,
  isUserMember = false,
  isUserAdmin = false,
}: CommunityCalendarViewProps) => {
  const theme = useTheme();
  const calendarRef = useRef<FullCalendar | null>(null);
  const queryClient = useQueryClient();

  const [calendarView, setCalendarView] = useState<CalendarViewType>('dayGridMonth');
  const [viewMenuAnchor, setViewMenuAnchor] = useState<null | HTMLElement>(null);
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
  });
  const [isAddEventOpen, setIsAddEventOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [viewingEvent, setViewingEvent] = useState<CalendarEvent | null>(null);
  const [currentViewDate, setCurrentViewDate] = useState(new Date());

  const { data: calendarEvents, isLoading } = useGetCommunityCalendarEvents(
    {
      community_id: communityId,
      start_date: dateRange.start,
      end_date: dateRange.end,
    },
    !!communityId
  );

  const getEventColor = (id: string | number) => {
    const colorKeys = Object.keys(EVENT_COLORS) as Array<keyof typeof EVENT_COLORS>;
    const hash = String(id)
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return EVENT_COLORS[colorKeys[hash % colorKeys.length]];
  };

  const events = useMemo(() => {
    if (!calendarEvents?.events) return [];
    return calendarEvents.events.map((event: CalendarEventModel) => {
      const color = getEventColor(event.id);
      return {
        id: event.id.toString(),
        title: event.title,
        start: event.start,
        end: event.end,
        allDay: event.all_day,
        backgroundColor: color.bg,
        borderColor: color.border,
        textColor: color.text,
        display: 'block',
        extendedProps: { color },
      };
    });
  }, [calendarEvents]);

  const isViewMenuOpen = Boolean(viewMenuAnchor);

  const handleViewMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setViewMenuAnchor(e.currentTarget);
  };
  const handleViewMenuClose = () => {
    setViewMenuAnchor(null);
  };

  const viewOptions = useMemo(
    () => [
      { value: 'dayGridMonth', label: 'Month view' },
      { value: 'timeGridWeek', label: 'Week view' },
      { value: 'timeGridDay', label: 'Day view' },
    ],
    []
  );

  const currentViewLabel = useMemo(
    () => viewOptions.find(opt => opt.value === calendarView)?.label || 'Month view',
    [calendarView, viewOptions]
  );

  const handlePrevClick = () => {
    calendarRef.current?.getApi().prev();
  };
  const handleNextClick = () => {
    calendarRef.current?.getApi().next();
  };
  const handleTodayClick = () => {
    calendarRef.current?.getApi().today();
  };

  const handleViewChange = (newView: CalendarViewType) => {
    setCalendarView(newView);
    handleViewMenuClose();
    calendarRef.current?.getApi().changeView(newView);
  };

  const handleDatesSet = useCallback(
    (info: { start: Date; end: Date }) => {
      const newStart = format(info.start, 'yyyy-MM-dd');
      const newEnd = format(info.end, 'yyyy-MM-dd');
      if (newStart !== dateRange.start || newEnd !== dateRange.end) {
        setDateRange({ start: newStart, end: newEnd });
      }

      // Update current view date to reflect the calendar's current view
      // For month view, use the middle of the date range
      // For week/day views, use the start date
      const viewDate =
        calendarView === 'dayGridMonth'
          ? new Date((info.start.getTime() + info.end.getTime()) / 2)
          : info.start;
      setCurrentViewDate(viewDate);
    },
    [dateRange, calendarView]
  );

  const dateRangeFormatted = useMemo(() => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
  }, [dateRange]);

  const currentMonthYear = useMemo(() => format(currentViewDate, 'MMMM yyyy'), [currentViewDate]);
  const currentWeek = useMemo(() => {
    const startOfYear = new Date(currentViewDate.getFullYear(), 0, 1);
    const daysElapsed = Math.floor(
      (currentViewDate.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000)
    );
    return Math.ceil((daysElapsed + startOfYear.getDay() + 1) / 7);
  }, [currentViewDate]);
  const currentDate = useMemo(() => {
    const today = new Date();
    const isCurrentMonth =
      currentViewDate.getMonth() === today.getMonth() &&
      currentViewDate.getFullYear() === today.getFullYear();

    // Show current day if viewing current month, otherwise show 1st of the viewed month
    const displayDate = isCurrentMonth
      ? today
      : new Date(currentViewDate.getFullYear(), currentViewDate.getMonth(), 1);

    return {
      day: format(displayDate, 'd'),
      month: format(currentViewDate, 'MMM').toUpperCase(),
    };
  }, [currentViewDate]);

  const calendarStyles = useMemo(
    () => ({
      '.custom-date-badge': {
        display: 'flex',
        flexDirection: 'column',
        width: '64px',
        height: '64px',
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.secondary[200]}`,
        backgroundColor: theme.palette.secondary.contrastText,
        marginRight: '12px',
      },
      '.date-badge-month': {
        backgroundColor: theme.palette.secondary[100],
        color: theme.palette.secondary[600],
        fontSize: '12px',
        fontWeight: 600,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '4px 8px 2px',
      },
      '.date-badge-day': {
        backgroundColor: theme.palette.secondary.contrastText,
        color: theme.palette.primary.main,
        fontSize: '18px',
        fontWeight: 700,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        padding: '1px 8px 3px',
      },
      '.week-badge': {
        backgroundColor: theme.palette.secondary[100],
        border: `1px solid ${theme.palette.secondary[400]}`,
        borderRadius: '6px',
        padding: '2px 6px',
        fontSize: '12px',
        fontWeight: 500,
        color: theme.palette.secondary.dark,
        marginLeft: '8px',
        display: 'inline-flex',
        alignItems: 'center',
      },
      '.fc': {
        fontFamily: 'Inter, -apple-system, sans-serif',
        '--fc-border-color': theme.palette.secondary[200],
        '--fc-button-text-color': theme.palette.secondary.dark,
        '--fc-button-bg-color': theme.palette.secondary.contrastText,
        '--fc-button-border-color': theme.palette.secondary[400],
        '--fc-button-hover-bg-color': theme.palette.secondary[100],
        '--fc-button-hover-border-color': theme.palette.secondary[400],
        '--fc-button-active-bg-color': theme.palette.secondary[100],
        '--fc-today-bg-color': theme.palette.secondary.contrastText,
        color: theme.palette.secondary.dark,
      },
      '.fc-view-harness': {
        backgroundColor: theme.palette.secondary.contrastText,
        borderRadius: '0px',
        border: '0px solid',
        overflow: 'hidden',
        boxShadow: 'none',
      },
      '.fc-scrollgrid': {
        backgroundColor: theme.palette.secondary.contrastText,
        border: 'none',
      },
      '.fc-scrollgrid-section-header': {
        display: 'none',
      },
      '.fc-scrollgrid-section-body td': {
        border: '1px solid',
        borderColor: theme.palette.secondary[200],
      },
      '.fc-daygrid-day': {
        border: '1px solid',
        borderColor: theme.palette.secondary[400],
        '&:hover': {
          backgroundColor: theme.palette.secondary[200],
        },
      },
      '.fc-day-today': {
        backgroundColor: theme.palette.secondary.contrastText,
        '&:hover': {
          backgroundColor: theme.palette.secondary[200],
          '& .fc-daygrid-day-frame': {
            backgroundColor: theme.palette.secondary[200],
          },
        },
        '& .fc-daygrid-day-number': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.info.contrastText,
          borderRadius: '50%',
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
      '.fc-day-other': {
        backgroundColor: theme.palette.secondary.contrastText,
      },
      '.fc-daygrid-day-frame': {
        padding: '8px',
      },
      '.fc-scrollgrid-sync-inner': {
        minHeight: '100px',
      },
      '.fc-daygrid-day-top': {
        justifyContent: 'flex-start',
      },
      '.fc-daygrid-day-number': {
        width: 'auto',
        padding: '8px',
        textAlign: 'left',
        fontSize: '12px',
        fontWeight: 600,
        color: theme.palette.secondary.dark,
        textDecoration: 'none',
      },
      '.fc-day-other .fc-daygrid-day-number': {
        color: theme.palette.secondary.dark,
        opacity: 0.7,
      },
      '.fc-h-event': {
        backgroundColor: '#FAFAFA',
        border: '1px solid #E9EAEB',
        borderRadius: '6px',
        color: '#414651',
        cursor: 'pointer',
        padding: '4px 8px',
        boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
        margin: '2px 0',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0px 2px 4px rgba(10, 13, 18, 0.1)',
        },
      },
      '.fc-event-title': {
        padding: '0',
        fontSize: '12px',
        fontWeight: 600,
        color: theme.palette.secondary.dark,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      '.fc-event-time': {
        fontSize: '12px',
        color: theme.palette.secondary.dark,
      },
      '.fc-daygrid-event-dot': {
        display: 'none',
      },
    }),
    [theme]
  );

  const handleEventCreated = () => {
    // Invalidate calendar events queries for this specific community
    if (communityId) {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY({
          community_id: communityId,
          start_date: dateRange.start,
          end_date: dateRange.end,
        }),
      });
    }
  };

  const handleDateClick = (info: { date: Date }) => {
    setSelectedDate(info.date);
    setIsAddEventOpen(true);
  };

  const handleEventClick = (info: { event: { id: string } }) => {
    const found = calendarEvents?.events.find(
      (e: CalendarEventModel) => e.id.toString() === info.event.id
    );
    if (found) {
      setViewingEvent(found);
    }
  };

  const handleEditEvent = (eventToEdit: DisplayEvent) => {
    setSelectedEvent(eventToEdit as unknown as CalendarEvent);
    setViewingEvent(null);
  };

  const handleEventUpdated = () => {
    // Invalidate calendar events queries for this specific community
    if (communityId) {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY({
          community_id: communityId,
          start_date: dateRange.start,
          end_date: dateRange.end,
        }),
      });
    }
  };

  const handleEventDeleted = () => {
    // Invalidate calendar events queries for this specific community
    if (communityId) {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY({
          community_id: communityId,
          start_date: dateRange.start,
          end_date: dateRange.end,
        }),
      });
    }
  };

  // Check if this is a first-time experience (no events exist at all)
  const hasNoEvents = !isLoading && (!calendarEvents?.events || calendarEvents.events.length === 0);
  const isFirstTimeExperience = hasNoEvents;

  // Show first-time experience if no events exist
  if (isFirstTimeExperience) {
    return (
      <MuiPageWrapper>
        <Box sx={{ p: 3 }}>
          <CommunityCalendarFirstTimeExperience
            onCreateEvent={() => {
              setSelectedDate(new Date());
              setIsAddEventOpen(true);
            }}
            canCreateEvent={isUserMember}
            isUserMember={isUserMember}
          />
        </Box>

        {isAddEventOpen && (
          <CommunityCalendarEventCreateModal
            onClose={() => {
              setIsAddEventOpen(false);
              setSelectedDate(null);
            }}
            communityId={communityId}
            initialDate={selectedDate}
            onEventCreated={handleEventCreated}
          />
        )}
      </MuiPageWrapper>
    );
  }

  return (
    <MuiPageWrapper>
      <GlobalStyles styles={calendarStyles} />
      <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: 'none',
            flex: 1,
            minHeight: 0,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{
              p: '20px 24px',
              backgroundColor: theme.palette.secondary.contrastText,
            }}
          >
            <Stack direction="row" alignItems="flex-start">
              <Box className="custom-date-badge">
                <Box className="date-badge-month">{currentDate.month}</Box>
                <Box className="date-badge-day">{currentDate.day}</Box>
              </Box>
              <Stack spacing={0.5}>
                <Stack direction="row" alignItems="center">
                  <Typography
                    sx={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: theme.palette.secondary.darker,
                    }}
                  >
                    {currentMonthYear}
                  </Typography>
                  <Box className="week-badge">Week {currentWeek}</Box>
                </Stack>
                <Typography
                  sx={{ color: theme.palette.secondary[600], fontSize: '14px', fontWeight: 400 }}
                >
                  {dateRangeFormatted}
                </Typography>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack
                direction="row"
                sx={{
                  overflow: 'hidden',
                  borderRadius: '24px',
                  boxShadow:
                    '0px 1px 2px 0px rgba(16, 24, 40, 0.05), inset 0px -2px 0px 0px rgba(10, 13, 18, 0.05), inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18)',
                  border: `1px solid ${theme.palette.secondary[400]}`,
                }}
              >
                <Button
                  sx={{
                    p: '8px 12px',
                    minWidth: 'auto',
                    borderRadius: '0',
                    borderRight: `1px solid ${theme.palette.secondary[400]}`,
                    color: theme.palette.secondary[600],
                    bgcolor: theme.palette.secondary.contrastText,
                  }}
                  onClick={handlePrevClick}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.5 15L7.5 10L12.5 5"
                      stroke={theme.palette.secondary[600]}
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Button>

                <Button
                  sx={{
                    p: '8px 16px',
                    color: theme.palette.secondary.dark,
                    bgcolor: theme.palette.secondary.contrastText,
                    fontWeight: 600,
                    fontSize: '14px',
                    borderRadius: '0',
                  }}
                  onClick={handleTodayClick}
                >
                  Today
                </Button>

                <Button
                  sx={{
                    p: '8px 12px',
                    minWidth: 'auto',
                    borderRadius: '0',
                    borderLeft: `1px solid ${theme.palette.secondary[400]}`,
                    color: theme.palette.secondary[600],
                    bgcolor: theme.palette.secondary.contrastText,
                  }}
                  onClick={handleNextClick}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.5 5L12.5 10L7.5 15"
                      stroke={theme.palette.secondary[600]}
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Button>
              </Stack>

              <Button
                variant="outlined"
                endIcon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke={theme.palette.secondary.dark}
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  p: '10px 14px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.secondary[400]}`,
                  color: theme.palette.secondary.dark,
                  boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
                }}
                onClick={handleViewMenuClick}
                aria-controls={isViewMenuOpen ? 'view-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isViewMenuOpen ? 'true' : undefined}
              >
                {currentViewLabel}
              </Button>

              <Menu
                id="view-menu"
                anchorEl={viewMenuAnchor}
                open={isViewMenuOpen}
                onClose={handleViewMenuClose}
                MenuListProps={{ 'aria-labelledby': 'view-selector-button' }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {viewOptions.map(option => (
                  <MenuItem
                    key={option.value}
                    onClick={() => handleViewChange(option.value as CalendarViewType)}
                    selected={calendarView === option.value}
                    sx={{
                      fontSize: '14px',
                      fontWeight: calendarView === option.value ? 600 : 400,
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>

              <Button
                variant="contained"
                startIcon={<PlusIcon />}
                sx={{
                  px: 2.5,
                  py: 1.25,
                  fontWeight: 600,
                  fontSize: '14px',
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                }}
                onClick={() => {
                  setSelectedDate(new Date());
                  setIsAddEventOpen(true);
                }}
              >
                Add event
              </Button>
            </Stack>
          </Stack>

          {/* Custom Weekday Header */}
          <Box sx={{ backgroundColor: theme.palette.secondary.contrastText }}>
            <Grid container columns={7}>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <Grid item xs={1} key={day}>
                  <Typography
                    sx={{
                      textAlign: 'center',
                      py: 0.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {day}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ p: 0 }}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={calendarView}
              headerToolbar={false}
              dayHeaderFormat={{ weekday: 'short' }}
              contentHeight="auto"
              height="auto"
              displayEventTime={false}
              dayMaxEvents={3}
              firstDay={1}
              fixedWeekCount={false}
              showNonCurrentDates
              timeZone="UTC"
              forceEventDuration
              defaultAllDay
              datesSet={handleDatesSet}
              dateClick={handleDateClick}
              events={events}
              eventContent={CalendarEventContent}
              eventClick={handleEventClick}
            />
          </Box>
        </Box>

        {isAddEventOpen && (
          <CommunityCalendarEventCreateModal
            onClose={() => {
              setIsAddEventOpen(false);
              setSelectedDate(null);
            }}
            communityId={communityId}
            initialDate={selectedDate}
            onEventCreated={handleEventCreated}
          />
        )}

        {selectedEvent && (
          <CommunityCalendarEventEditModal
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onEventDeleted={handleEventDeleted}
            onEventUpdated={handleEventUpdated}
          />
        )}

        {viewingEvent && (
          <CommunityEventDetailsModal
            event={viewingEvent as unknown as DisplayEvent}
            onClose={() => setViewingEvent(null)}
            onEditClick={handleEditEvent}
            onEventUpdated={handleEventUpdated}
          />
        )}
      </Box>
    </MuiPageWrapper>
  );
};

export default CommunityCalendarView;
